import { useEffect, useRef, useState } from "react";
import { Button } from "components/elements";
import { useCurrentUser } from "hooks/useCurrentUser";

interface OrgBearer {
    organizationId?: number | null;
}

interface UseExternalOrgConfirmOpts {
    /**
     * Users already on the discussion (selected recipients, or existing
     * participants) — used to know which external orgs we already span.
     */
    existingUsers: OrgBearer[];
}

/**
 * Adds a confirmation step when adding a recipient would expand a discussion
 * to a second distinct external organization. Own-org and one external org are
 * allowed silently; the 2nd+ external org prompts. Mirrors the chart-sharing
 * behavior from PRTL-1279 (see screens/charts/useExternalOrgConfirm) for the
 * discussions message flow (PRTL-1324).
 */
export function useExternalOrgConfirm({ existingUsers }: UseExternalOrgConfirmOpts) {
    const currentUser = useCurrentUser();
    // PRTL-1348: compare against the org this session is scoped to, not the
    // legacy flat organizationId (wrong for multi-org users whose active org
    // isn't their oldest membership).
    const ownOrgId = currentUser?.activeOrganizationId ?? currentUser?.organizationId;
    const [pending, setPending] = useState<{ resolve: (confirmed: boolean) => void } | null>(null);

    // Resolve a dangling confirm as "cancelled" if the host component unmounts
    // while we're awaiting a decision (e.g. the parent modal is closed via
    // Escape/backdrop) — otherwise the caller's await would hang forever.
    const pendingRef = useRef(pending);
    useEffect(() => {
        pendingRef.current = pending;
    }, [pending]);
    useEffect(
        () => () => {
            pendingRef.current?.resolve(false);
        },
        [],
    );

    const externalOrgId = (u: OrgBearer): number | null => {
        if (u.organizationId == null || ownOrgId == null) return null;
        return u.organizationId === ownOrgId ? null : u.organizationId;
    };

    const existingExternalOrgIds = new Set(
        existingUsers.map(externalOrgId).filter((id): id is number => id !== null),
    );

    // Would adding these users expand sharing to a 2nd+ distinct external org?
    const additionNeedsConfirm = (users: OrgBearer[]): boolean => {
        const seen = new Set(existingExternalOrgIds);
        for (const u of users) {
            const id = externalOrgId(u);
            if (id === null) continue; // own org / no org — never warns
            if (seen.has(id)) continue; // already sharing with this org
            // Own-org + 1 external is allowed silently; the 2nd+ external prompts.
            if (seen.size >= 1) return true;
            seen.add(id);
        }
        return false;
    };

    const requestConfirm = (): Promise<boolean> =>
        new Promise((resolve) => setPending({ resolve }));

    /** Per-add guard (one user at a time, e.g. a recipient combobox). */
    const onBeforeAdd = (u: OrgBearer): Promise<boolean> =>
        additionNeedsConfirm([u]) ? requestConfirm() : Promise.resolve(true);

    /** Bulk guard (many users committed at once, e.g. a save button). */
    const confirmAdditions = (users: OrgBearer[]): Promise<boolean> =>
        additionNeedsConfirm(users) ? requestConfirm() : Promise.resolve(true);

    const handleConfirm = () => {
        pending?.resolve(true);
        setPending(null);
    };

    const handleCancel = () => {
        pending?.resolve(false);
        setPending(null);
    };

    const modal = pending ? (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
            role="dialog"
            aria-modal="true"
            aria-labelledby="external-org-confirm-title"
            aria-describedby="external-org-confirm-desc"
        >
            <div className="w-[400px] max-w-[90vw] rounded-xl bg-white p-6 shadow-xl">
                <h3
                    id="external-org-confirm-title"
                    className="mb-2 text-base font-semibold text-gray-900"
                >
                    Message outside your organization?
                </h3>
                <p id="external-org-confirm-desc" className="mb-6 text-sm text-gray-600">
                    This recipient is outside the current organization. Are you sure you want to
                    message across multiple organizations?
                </p>
                <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirm}>Proceed</Button>
                </div>
            </div>
        </div>
    ) : null;

    return { onBeforeAdd, confirmAdditions, modal };
}
